import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet } from "../../apis/request";
import Loading from "../../assets/uxnicon/loadingIcon.png";
import AdminLayout from "./AdminLayout";
import secureLocalStorage from "react-secure-storage";
import { formatServerTime } from "../../utils/serverTime";

// FCM 발송 기록 탭 — 누가(sent_by) 누구에게(target) 어떤 문구를 보냈고 결과가 어땠는지 (fcm_send_log, 최신순).
// 열람: 오원준(5002)·장재용(4952)만 (AdminLayout 메뉴 + 서버 /api/admin/fcm/logs 둘 다 제한).
export const FCM_LOG_VIEWER_IDS = [4952, 5002];
export const canViewFcmLogs = () =>
  Number(secureLocalStorage.getItem("role")) === 1001
  && FCM_LOG_VIEWER_IDS.includes(Number(secureLocalStorage.getItem("authUser")));

const PAGE_SIZE = 20;

const RESULT_LABEL = {
  SENT: { text: "발송 성공", color: "#2e7d32", bg: "#e8f5e9" },
  FAILED: { text: "발송 실패", color: "#c62828", bg: "#ffebee" },
  NO_TOKEN: { text: "토큰 없음", color: "#ef6c00", bg: "#fff3e0" },
  USER_NOT_FOUND: { text: "유저 없음", color: "#6a1b9a", bg: "#f3e5f5" },
  BAD_REQUEST: { text: "잘못된 요청", color: "#455a64", bg: "#eceff1" },
};

const thBase = {
  backgroundColor: "#787397", color: "#fff", fontWeight: "bold", fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px", borderRight: "1px solid #EBEBEB", textAlign: "center", whiteSpace: "nowrap",
};
const tdBase = {
  backgroundColor: "#fff", textAlign: "center", borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB", padding: "8px 12px",
};
const pageBtn = (active, disabled) => ({
  minWidth: "34px", padding: "6px 10px",
  border: active ? "1px solid #787397" : "1px solid #d5d0dd", borderRadius: "6px",
  background: active ? "#787397" : "#fff", color: active ? "#fff" : "#5E2589",
  fontWeight: active ? "bold" : "normal", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1,
});

const fmtDt = (v) => formatServerTime(v, "YYYY-MM-DD HH:mm:ss", "-"); // 서버 ISO UTC → 브라우저 시간대

const AdminFcmLogList = () => {
  const [pageResp, setPageResp] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const allowed = canViewFcmLogs();

  const callApi = async (p) => {
    setError(null);
    try {
      const res = await agmsClientRequestGet(`/api/admin/fcm/logs?page=${p}&size=${PAGE_SIZE}`);
      if (res && Array.isArray(res.content)) setPageResp(res);
      else {
        setError("발송 기록을 불러오지 못했습니다. (권한 또는 서버 오류)");
        setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
      }
    } catch (e) {
      setError("발송 기록을 불러오지 못했습니다. (권한 또는 서버 오류)");
      setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
    }
  };

  useEffect(() => {
    if (!allowed) { setPageResp({ content: [], totalElements: 0, totalPages: 1, page: 1, size: PAGE_SIZE }); return; }
    callApi(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, allowed]);

  if (pageResp === null) {
    return (
      <AdminLayout>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px" }}>
          <img src={Loading} alt="loading..." style={{ width: "50px", height: "50px", animation: ".75s linear infinite spinner-border" }} />
        </div>
      </AdminLayout>
    );
  }

  if (!allowed) {
    return (
      <AdminLayout>
        <div style={{ padding: "40px", color: "#84818A" }}>이 화면을 볼 수 있는 권한이 없습니다.</div>
      </AdminLayout>
    );
  }

  const content = pageResp.content ?? [];
  const total = pageResp.totalElements ?? 0;
  const totalPages = Math.max(1, pageResp.totalPages ?? 1);
  const currentPage = Math.min(page, totalPages);

  return (
    <AdminLayout>
      <div style={{ width: "100%" }}>
        <div style={{ marginBottom: "17px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: "bold", fontSize: "20px" }}>FCM 발송 기록</div>
          <div style={{ color: "#84818A" }}>총 {total}건 · 최신순</div>
        </div>

        {error && <div style={{ color: "#c62828", marginBottom: "12px" }}>{error}</div>}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, border: "1px solid #EBEBEB" }}>
            <thead>
              <tr>
                <th style={thBase}>시각</th>
                <th style={thBase}>누른 사람</th>
                <th style={thBase}>받는 사람</th>
                <th style={thBase}>종류</th>
                <th style={{ ...thBase, textAlign: "left" }}>메시지</th>
                <th style={thBase}>결과</th>
              </tr>
            </thead>
            <tbody>
              {content.length === 0 && (
                <tr><td style={{ ...tdBase, padding: "30px", color: "#84818A" }} colSpan={6}>발송 기록이 없습니다.</td></tr>
              )}
              {content.map((r) => {
                const rl = RESULT_LABEL[r.result] || { text: r.result || "-", color: "#455a64", bg: "#eceff1" };
                return (
                  <tr key={r.id}>
                    <td style={{ ...tdBase, whiteSpace: "nowrap" }}>{fmtDt(r.createdAt)}</td>
                    <td style={tdBase}>
                      <div>{r.sentByName || "-"}</div>
                      <div style={{ fontSize: "12px", color: "#84818A" }}>{r.sentByEmail || ""}{r.sentByUserId ? ` (${r.sentByUserId})` : ""}</div>
                    </td>
                    <td style={tdBase}>
                      <div>{r.targetEmail || "-"}</div>
                      <div style={{ fontSize: "12px", color: "#84818A" }}>{r.targetUserId ? `(${r.targetUserId})` : ""}</div>
                    </td>
                    <td style={tdBase}>{r.eventType || "-"}</td>
                    <td style={{ ...tdBase, textAlign: "left", maxWidth: "420px", wordBreak: "break-all" }}>{r.message || "-"}</td>
                    <td style={tdBase}>
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: "bold", color: rl.color, background: rl.bg }}>{rl.text}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "16px" }}>
          <button style={pageBtn(false, currentPage <= 1)} disabled={currentPage <= 1} onClick={() => setPage(1)}>«</button>
          <button style={pageBtn(false, currentPage <= 1)} disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((n) => Math.abs(n - currentPage) <= 3)
            .map((n) => (
              <button key={n} style={pageBtn(n === currentPage, false)} onClick={() => setPage(n)}>{n}</button>
            ))}
          <button style={pageBtn(false, currentPage >= totalPages)} disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>›</button>
          <button style={pageBtn(false, currentPage >= totalPages)} disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default withRouter(AdminFcmLogList);
